<template>
  <div class="payment-summary">
    <div class="tip-info">
      <div v-if="isRoundTrip">
        <div class="row margin-b-s">
          <div class="small-4 columns summary-label">
            {{ $t('payment.leg') }} 1:&nbsp;
          </div>
          <div class="small-8 columns text-left summary-value">
            <strong v-if="discountValue && discountCode.code">
              <s>{{ applyCurrency(addZero(leg1DcOrig)) }}</s>
              <span>{{ applyCurrency(addZero(subTotalDiscounted)) }}</span>
            </strong>
            <strong v-else>
              <s v-if="leg1DcOrig!=leg1Amount">{{ applyCurrency(addZero(leg1DcOrig)) }}</s>
              <span>{{ applyCurrency(addZero(leg1Amount)) }}</span>
            </strong>
          </div>
        </div>
        <div class="row margin-b-s">
          <div class="small-4 columns summary-label">
            {{ $t('payment.leg') }} 2:&nbsp;
          </div>
          <div class="small-8 columns text-left summary-value">
            <strong v-if="discountValue && discountCode.code">
              <s>{{ applyCurrency(addZero(leg2DcOrig)) }}</s>
              <span>{{ applyCurrency(addZero(leg2Amount)) }}</span>
            </strong>
            <strong v-else>
              <s v-if="leg2DcOrig!=leg2Amount">{{ applyCurrency(addZero(leg2DcOrig)) }}</s>
              <span>{{ applyCurrency(addZero(leg2Amount)) }}</span>
            </strong>
          </div>
        </div>
      </div>

      <div class="row margin-b-s" v-else>
        <div class="small-4 columns summary-label">
          {{ $t('payment.fare') }}:&nbsp;
        </div>
        <div class="small-8 columns text-left summary-value">
          <strong v-if="discountValue && discountCode.code">
            <s>{{ applyCurrency(addZero(leg1DcOrig)) }}</s>
            <span>{{ applyCurrency(addZero(subTotalDiscounted)) }}</span>
          </strong>
          <strong v-else>
            <s v-if="!mtech.enabled && leg1DcOrig!=subtotal && leg1DcOrig!=-1">{{ applyCurrency(addZero(leg1DcOrig)) }}</s>
            <span>{{ applyCurrency(addZero(subtotal)) }}</span>
          </strong>
        </div>
      </div>

      <div class="row margin-b-s" v-if="showConvenienceFee">
        <div class="small-4 columns summary-label" style="font-size:14px">
          Convenience Fee:
        </div>
        <div class="small-8 columns text-left summary-value">
          <strong>
            <span>{{ applyCurrency(addZero(convenienceFee)) }}</span>
          </strong>
        </div>
      </div>

      <div class="row margin-b-s" v-if="showTravelInsurance">
        <div class="small-4 columns summary-label">
          <span>{{ $t('payment.riderFee') }}:&nbsp;</span>
        </div>
        <div class="small-8 columns text-left summary-value">
          <strong>{{ applyCurrency(addZero(travelInsurance)) }}</strong>
        </div>
      </div>

      <div class="row margin-b-s" v-if="showAirportFee">
        <div class="small-4 columns summary-label">
          <span>{{ $t('payment.airportFee') }}&nbsp;</span>
        </div>
        <div class="small-8 columns text-left summary-value">
          <strong>{{ applyCurrency(addZero(airportFee)) }}</strong>
        </div>
      </div>

      <div class="row margin-b-s" v-if="showGasFee">
        <div class="small-4 columns summary-label">
          <span>{{ $t('payment.fuelSurcharge') }}:&nbsp;</span>
        </div>
        <div class="small-8 columns text-left summary-value">
          <strong>{{ applyCurrency(addZero(gasFee)) }}</strong>
        </div>
      </div>

      <div class="row margin-b-s" v-if="showMeetAndGreet">
        <div class="small-4 columns summary-label">
          {{ $t('payment.mng') }}&nbsp;
        </div>
        <div class="small-8 columns text-left summary-value">
          <strong>{{ applyCurrency(addZero(meetAndGreetFee)) }}</strong>
        </div>
      </div>

      <div class="margin-b-s" v-if="!isAsap && !lyft.enabled">
        <div class="row">
          <div class="small-4 columns summary-label">{{ $t('payment.tip') }}:&nbsp;</div>
          <div class="small-8 columns text-left summary-value">
            <strong v-if="currency.code === 'USD'">{{ applyCurrency(addZero(tip), true, false) }}</strong>
            <strong v-else>{{ xcurrency.format({code: currency.code, val: tipIntl}) }}</strong>
            <a href="javascript:;" @click="toggleTipForm" class="add-tip">{{ tipLabel }}</a>
          </div>
        </div>
        <div class="row padding-h-m">
          <div class="small-8 small-offset-4 medium-6 medium-offset-6 columns">
            <div v-bind:class="['tip-form' + (showTipForm? '': ' hidden')]">
              <div class="field-group">
                <select v-bind:class="['field-text field-select' + (showCustomTip? ' hide': '')]"
                  v-on:change="onTipSelected" v-model="selectedTip">
                  <option value="0">{{ applyCurrency('0.00', true, false) }}</option>
                  <option v-bind:value="tipBreakdown.ten">{{ applyCurrency(addZero(tipBreakdown.ten), true, false) }} (10%)</option>
                  <option v-bind:value="tipBreakdown.fifteen">{{ applyCurrency(addZero(tipBreakdown.fifteen), true, false) }} (15%)</option>
                  <option v-bind:value="tipBreakdown.twenty">{{ applyCurrency(addZero(tipBreakdown.twenty), true, false) }} (20%)</option>
                  <option value="custom">{{ $t('payment.custom') }}</option>
                </select>
                <input type="number" pattern="\d*" :placeholder="$t('payment.enterTip')"
                  v-focus="showCustomTip" v-model.number="customTip" v-if="currency.code === 'USD'"
                  @keypress="isNumber" @blur="validateTip('customTip')"
                  :class="['field-text custom-tip' + (showCustomTip? '': ' hide')]" />
                <input type="number" pattern="\d*" :placeholder="$t('payment.enterTip')"
                  v-focus="showCustomTip" v-model.number="tipIntl" v-else
                  @keypress="isNumber" @blur="validateTip('tipIntl')"
                  :class="['field-text custom-tip' + (showCustomTip? '': ' hide')]" />
                <a class="op-btn btn-block btn-success" v-on:click="addTip">{{ tipLabel }}</a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="mtech.enabled">
        <div class="row margin-b-s" v-for="(fee, i) in mtechFees" :key="i">
          <div class="small-4 columns summary-label">
            <span>{{fee.description}}:</span>
          </div>
          <div class="small-8 columns text-left summary-value">
            <strong>{{ formatValue(fee.amount) }}</strong>
          </div>
        </div>
      </div>
    </div>

    <div :class="['promo-info' + (showPromoCodeInput? '': ' hide')]">
      <div class="row padding-h-m">
        <div v-if="flashData.type == 'redeem'" :class="['alert-wrap showhide-fadeslide', (flashData.message === ''? 'hide' : '')]">
          <div :class="[ 'alert margin-b-s', flash.notice ]" v-html="flashData.message"></div>
        </div>
      </div>
      <div class="row">
        <div class="small-4 columns summary-label">
          <p class="margin-a-z promo-label">{{ $t('payment.credits') }}&nbsp;</p>
        </div>
        <div class="small-8 columns text-left summary-value">
          <p class="margin-a-z promo-value">
            <strong>({{ applyCurrency(addZero(credits), true, false) }})</strong>
          </p>
        </div>
      </div>
      <div class="row margin-t-s padding-r-s">
        <div class="small-12 columns">
          <div class="field-group redeemWrap">
            <div class="field-combo">
              <input class="field-text" placeholder="Promo/Discount code" v-model="promoCode" :disabled="redeemLoading" />
              <div class="field-button btn-dark text-center" v-on:click="redeemCode">
                <button v-bind:class="['padding-a-s redeemBtn', (redeemLoading? 'loading' : '')]">
                  <span class="original-label" v-show="!redeemLoading">{{ $t('payment.redeem') }}</span>
                  <div class="loaderx" v-show="redeemLoading">
                    <icon name="refresh" spin class="magin-r-xs"></icon>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="price-info">
      <div class="row">
        <div class="small-4 columns">
          <h2 class="font-normal margin-a-z">{{ $t('payment.total') }}&nbsp;</h2>
        </div>
        <div class="small-8 columns text-left summary-value">
          <h2 class="margin-a-z total" v-if="currency.code === 'USD'">{{ applyCurrency(addZero(total)) }}</h2>
          <h2 class="margin-a-z total" v-else>{{ totalIntl }}</h2>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* global CONFIG */
import xcurrency from '@/modules/api/currency'

export default {
  name: 'payment-summary-form',
  props: [ 'flash', 'leg1', 'leg2', 'user', 'currentLeg' ],
  data () {
    return {
      showTipForm: false,
      showCustomTip: false,
      selectedTip: '0',
      customTip: 0,
      promoCode: '',
      redeemLoading: false,
      flashNoticeType: '',
      travelInsuranceLabel: this.$t('payment.riderFee'),
      redeemTrigger: false,
      xcurrency: xcurrency
    }
  },
  computed: {
    flashData () { return this.flash ? this.flash : { type: '', message: '' } },
    leg1Amount () {
      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      let result = 0
      let priceType = this.lyft.enabled ? 'prices' : 'roundtrip'
      if (leg1.selectedService) {
        let serviceData = leg1.services[leg1.selectedService]
        let servicePricing = serviceData[priceType][leg1.passenger - 1]
        result = servicePricing.price
      }
      return result
    },
    leg2Amount () {
      let rsvp = this.$store.state.reservation
      if (!rsvp.isRoundtrip) return 0
      let leg2 = rsvp.leg2
      let result = 0
      let priceType = this.lyft.enabledLeg2 ? 'prices' : 'roundtrip'
      if (leg2.selectedService) {
        let serviceData = leg2.services[leg2.selectedService]
        let servicePricing = serviceData[priceType][leg2.passenger - 1]
        result = servicePricing.price
      }
      return result
    },
    subtotal () {
      let result = -1
      if (this.mtech.enabled) {
        let rsvp = this.$store.state.reservation.leg1
        if (rsvp.selectedService) {
          let service = this.mtech.leg1.services[rsvp.selectedService]
          return service.price.breakdown[0].amount + service.price.addMarkup
        }
        return 0
      }

      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      let leg2 = rsvp.leg2
      let priceType = this.lyft.enabled ? 'prices' : 'roundtrip'
      if (leg1.selectedService) {
        let serviceData = leg1.services[leg1.selectedService]
        let servicePricing = serviceData.prices[leg1.passenger - 1]
        if (rsvp.isRoundtrip) servicePricing = serviceData[priceType][leg1.passenger - 1]
        result = servicePricing.price
      }
      if (rsvp.isRoundtrip && leg2.selectedService) {
        let serviceData = leg2.services[leg2.selectedService]
        let servicePricing = serviceData[priceType][leg2.passenger - 1]
        result += servicePricing.price
      }

      // if (this.leg1 !== null) {
      //   result = this.leg1.amount
      // }
      // if (this.leg2 && Object.keys(this.leg2).length > 0) {
      //   result = result + this.leg2.amount
      // }
      return result
    },
    total () {
      if (this.mtech.enabled) {
        let total = this.subtotal + this.tip
        let rsvp = this.$store.state.reservation.leg1
        if (rsvp.selectedService) {
          let breakdown = this.mtech.leg1.services[rsvp.selectedService].price.breakdown
          for (let i = 1; i < breakdown.length; i++) {
            total += breakdown[i].amount
          }
        }
        total = total - this.credits
        return total >= 0 ? total : 0
      }

      let result = (this.tip + this.subtotal + this.meetAndGreetFee + this.airportFee + this.gasFee + this.travelInsurance) - this.credits
      if (this.showConvenienceFee) result += this.convenienceFee
      if (result < 0) { result = 0 }
      return parseFloat(result)
    },
    tipBreakdown () {
      let ten = Math.round(this.subtotal * 0.10)
      let fifteen = Math.round(this.subtotal * 0.15)
      let twenty = Math.round(this.subtotal * 0.20)

      return {
        ten: ten,
        fifteen: fifteen,
        twenty: twenty
      }
    },
    tip () { return this.$store.state.tip.amount },
    tipLabel () { return this.$store.state.tip.label },
    credits () { return this.$store.state.credits },
    showPromoCodeInput () { return this.$store.state.showPromoCodeInput },
    isRoundTrip () { return this.$store.state.reservation.isRoundtrip },
    meetAndGreetFee () {
      let result = 0
      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      let leg2 = rsvp.leg2

      if (leg1.isAsap) {
        return 0
      }

      if (leg1.isAirportPickup) {
        result += parseInt(leg1.pickupFee)
      }
      if (rsvp.isRoundtrip && leg2.isAirportPickup) {
        result += parseInt(leg2.pickupFee)
      }
      return result
    },
    showMeetAndGreet () { return this.meetAndGreetFee > 0 && !this.leg1.isAsap },
    showAirportFee () { return this.airportFee > 0 },
    showGasFee () { return this.gasFee > 0 },
    airportFee () {
      if (this.mtech.enabled) return 0
      let result = 0
      // if (this.leg1 != null && this.leg1.airportFee) {
      //   result += parseFloat(this.leg1.airportFee)
      // }
      // if (this.leg2 && Object.keys(this.leg2).length && this.leg2.airportFee) {
      //   result += parseFloat(this.leg2.airportFee)
      // }
      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      if (leg1.selectedService) {
        result = leg1.services[leg1.selectedService].airportFee
      }
      let leg2 = rsvp.leg2
      if (rsvp.isRoundtrip && leg2.selectedService) {
        result += leg2.services[leg2.selectedService].airportFee
      }
      return result
    },
    gasFee () {
      if (this.mtech.enabled) return 0

      let result = 0
      let rsvp = this.$store.state.reservation
      if (this.leg1 != null && rsvp.leg1.selectedService && rsvp.leg1.services[rsvp.leg1.selectedService].gasFee) {
        result += parseFloat(rsvp.leg1.services[rsvp.leg1.selectedService].gasFee)
      }
      if (rsvp.isRoundtrip && rsvp.leg2.selectedService && rsvp.leg2.services[rsvp.leg2.selectedService].gasFee) {
        result += parseFloat(rsvp.leg2.services[rsvp.leg2.selectedService].gasFee)
      }
      return result
    },
    showTravelInsurance () { return this.travelInsurance > 0 },
    travelInsurance () {
      if (this.mtech.enabled) return 0

      let rsvp = this.$store.state.reservation
      let total = 0

      if (rsvp.leg1.selectedService) {
        total = parseFloat(rsvp.leg1.services[rsvp.leg1.selectedService].travelInsurance)
      }

      if (rsvp.isRoundtrip && rsvp.leg2.selectedService && rsvp.leg2.selectedService) {
        total += parseFloat(rsvp.leg2.services[rsvp.leg2.selectedService].travelInsurance)
      }
      return total
    },
    mtech () { return this.$store.state.reservation.mtech },
    mtechFees () {
      let rsvp = this.$store.state.reservation
      let fees = [...this.mtech.leg1.services[rsvp.leg1.selectedService].price.breakdown]
      fees.shift()
      return fees
    },
    _lang () { return this.$i18n.locale },
    discountCode () { return this.$store.state.discountCode },
    discountValue () {
      if (!this.discountCode.id) return 0
      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      return leg1.services[leg1.selectedService].prices[leg1.passenger - 1].discount
    },
    routeName () { return this.$route.name },
    _getTotal () { return this.$store.state.tracking.opoliCNS.getTotal },
    _getTotal2 () { return this.$store.state.tracking.opoliCNS.getTotal2 },
    subTotalDiscounted () {
      let result = -1
      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      if (leg1.selectedService) {
        let serviceData = leg1.services[leg1.selectedService]
        let pricing = this.isRoundTrip ? 'roundtrip' : 'prices'
        let servicePricing = serviceData[pricing][leg1.passenger - 1]
        result = servicePricing.price
      }
      return result
    },
    leg1DcOrig () {
      let result = -1
      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      let priceType = this.lyft.enabled || leg1.isAsap ? 'prices' : 'roundtrip'
      if (leg1.selectedService) {
        let serviceData = rsvp.mtech.enabled ? rsvp.mtech.services[rsvp.selectedService] : leg1.services[leg1.selectedService]
        let servicePricing = serviceData[priceType][leg1.passenger - 1]
        result = servicePricing.original
      }
      return result
    },
    leg2DcOrig () {
      let result = -1
      let rsvp = this.$store.state.reservation.leg2
      let priceType = this.lyft.enabledLeg2 ? 'prices' : 'roundtrip'
      if (rsvp.selectedService) {
        let serviceData = rsvp.services[rsvp.selectedService]
        let servicePricing = serviceData[priceType][rsvp.passenger - 1]
        result = servicePricing.original
      }
      return result
    },
    currency () { return this.$store.state.other.currency },
    tipIntl: {
      get () { return this.$store.state.tip.intl },
      set (x) { this.$store.commit('setTipIntl', x) }
    },
    totalIntl () {
      if (this.mtech.enabled) {
        let total = this.applyCurrency(this.subtotal.toFixed(2), false)
        total += this.tipIntl
        let rsvp = this.$store.state.reservation.leg1
        if (rsvp.selectedService) {
          let breakdown = this.mtech.leg1.services[rsvp.selectedService].price.breakdown
          for (let i = 1; i < breakdown.length; i++) {
            total += this.applyCurrency(breakdown[i].amount, false)
          }
        }
        total = total - this.applyCurrency(this.credits, false)
        total = total >= 0 ? total : 0
        return xcurrency.format({
          code: this.currency.code,
          val: total
        })
      }

      let result = this.applyCurrency(this.subtotal.toFixed(2), false)
      result += this.tipIntl
      result += this.applyCurrency(this.meetAndGreetFee, false)
      result += this.applyCurrency(this.airportFee, false)
      result += this.applyCurrency(this.gasFee, false)
      result += this.applyCurrency(this.travelInsurance, false)
      if (this.showConvenienceFee) result += this.applyCurrency(this.convenienceFee, false)
      result -= parseFloat((this.credits * this.currency.value).toFixed(2))
      if (result < 0) { result = 0 }
      return xcurrency.format({
        code: this.currency.code,
        val: result
      })
    },
    isAsap () { return this.$store.state.reservation.leg1.isAsap },
    showConvenienceFee () {
      // let thisBrand = Object.values(CONFIG.Brands).find(x => x.id === this.$store.state.brandId)
      // return thisBrand && thisBrand.enableConvenieceFee
      return this.convenienceFee > 0
    },
    convenienceFee () {
      if (this.mtech.enabled) return 0
      let rsvp = this.$store.state.reservation
      let leg1 = rsvp.leg1
      let leg2 = rsvp.leg2
      let total = 0
      let priceType = rsvp.isRoundtrip ? 'roundtrip' : 'prices'
      if (leg1.selectedService) {
        let serviceData = leg1.services[leg1.selectedService]
        if (this.lyft.enabled) priceType = 'prices'
        let servicePricing = serviceData[priceType][leg1.passenger - 1]
        let price = servicePricing.price
        if (serviceData.portalFee) {
          if (serviceData.portalFeeType === '%') total = parseFloat((price * (serviceData.portalFee / 100)).toFixed(2))
          else total = parseFloat(serviceData.portalFee)
        }
      }
      if (rsvp.isRoundtrip && leg2.selectedService) {
        let serviceData = leg2.services[leg2.selectedService]
        let priceType = !this.lyft.enabledLeg2 ? 'roundtrip' : 'prices'
        let servicePricing = serviceData[priceType][leg2.passenger - 1]
        let price = servicePricing.price
        // total += parseFloat((price * 0.1).toFixed(2))
        if (serviceData.portalFee) {
          if (serviceData.portalFeeType === '%') total += parseFloat((price * (serviceData.portalFee / 100)).toFixed(2))
          else total += parseFloat(serviceData.portalFee)
        }
      }
      if (!total) return 0
      return parseFloat(total.toFixed(2))
    },
    lyft () { return this.$store.state.reservation.lyft }
  },
  methods: {
    toggleTipForm () {
      this.showTipForm = !this.showTipForm
      if (!this.showTipForm) {
        this.showCustomTip = false
        this.selectedTip = '0'
        this.customTip = ''
      }
    },
    addZero (val) {
      return parseFloat(val).toFixed(2)
    },
    onTipSelected () {
      if (this.selectedTip === 'custom') {
        this.showCustomTip = true
      } else {
        this.showCustomTip = false
      }
    },
    addTip () {
      this.$store.commit('setIsManualTip', true)
      let tip = 0
      if (this.selectedTip === 'custom') {
        if (this.customTip !== '') tip = parseFloat(this.customTip)
        if (this.currency.code !== 'USD' && this.tipIntl > 0) {
          if (this.customTip !== '') tip = parseFloat(this.tipIntl)
          tip = parseFloat((tip / this.currency.value).toFixed(2))
        }
      } else {
        tip = parseFloat(this.selectedTip)
        this.tipIntl = parseFloat((tip * this.currency.value).toFixed(2))
      }
      this.$store.commit('setTipAmount', tip)

      let label = this.$t('payment.addTip')
      if (tip > 0) {
        label = this.$t('payment.changeTip')
      }
      this.$store.commit('setTipLabel', label)

      this.toggleTipForm()
    },
    redeemCode () {
      if (this.mtech.enabled) {
        this.$root.flash = { notice: 'error', type: 'redeem', message: 'Promo codes are not applicable for this location.' }
        return
      }
      if (this.promoCode) {
        this.redeemLoading = true
        this.$root.$emit('redeemCode', { code: this.promoCode })
        this.redeemTrigger = true
      }
    },
    recheckTipLabel () {
      let label = this.tip > 0 ? this.$t('payment.changeTip') : this.$t('payment.addTip')
      this.$store.commit('setTipLabel', label)
    },
    formatValue (x) {
      let val = x < 0 ? this.addZero(Math.abs(x)) : this.addZero(x)
      return x < 0 ? `-${this.applyCurrency(val)}` : this.applyCurrency(val)
    },
    pickupTypeLabel (type) {
      let rsvp = this.$store.state.reservation[this.currentLeg]
      if (
        !this.mtech.enabled &&
        (rsvp.pickup.geofenceId === CONFIG.laxGeofenceId || CONFIG.showArrivalInstruction.indexOf(rsvp.pickup.geofenceId) > -1) &&
        rsvp.services[rsvp.selectedService]
      ) {
        let pickupLabel = this.$store.state.instructions
        return rsvp.services[rsvp.selectedService].licenseType === 'tcp' ? pickupLabel.tcp.pickupLabel : pickupLabel.tnc.pickupLabel
      }

      switch (type) {
        case 'Curbside': return this.$t('rsvpDetails.curbside')
        case 'MeetAndGreet': return this.$t('rsvpDetails.mng')
        default: return ''
      }
    },
    applyCurrency (x, useSymbol = true, zeroFree = true) {
      x = parseFloat(x)
      if (x < 0) x = 0
      // if (x === 0 && useSymbol && zeroFree) return 'FREE'
      x = x * this.currency.value
      if (!useSymbol) {
        return parseFloat(x.toFixed(2))
      }
      return xcurrency.format({
        code: this.currency.code,
        val: x.toFixed(2)
      }, useSymbol)
      // return x >= 0 ? `${this.currency.symbol}${x.toFixed(2)}` : `-${this.currency.symbol}${Math.abs(x).toFixed(2)}`
    },
    isNumber (evt) {
      let charCode = (evt.which) ? evt.which : evt.keyCode
      if (charCode > 31 && ((charCode < 48 || charCode > 57) && charCode !== 46)) {
        evt.preventDefault()
        return
      }
      return true
    },
    validateTip (field) {
      let val = this[field]
      val = val.toString().replaceAll('-', '')
      this[field] = parseFloat(val)
    }
  },
  watch: {
    credits () {
      this.redeemLoading = false
      this.promoCode = ''
    },
    flash () {
      if (this.flash.type === 'redeem') {
        this.redeemLoading = false
        this.flashNoticeType = this.flash.notice
        if (this.flash.notice === 'success') {
          this.promoCode = ''
        }
      }
    },
    tip () {
      this.recheckTipLabel()
    },
    _lang () {
      this.recheckTipLabel()
      this.travelInsuranceLabel = this.$t('payment.riderFee')
    },
    _getTotal (val) {
      if (val) {
        if (window.opoliCNS) {
          val.segment['Total Price'] = this.total

          if (val.key === 'Review Page') {
            val.segment['Promo Code'] = this.promoCode
            val.segment['Redeem'] = this.redeemTrigger ? 'Yes' : 'No'
          }
          window.opoliCNS('events', val)
        }
        this.$store.commit('tracking/setOpoliCnsData', {getTotal: ''})
      }
    },
    'currency.code': function (val) {
      if (val !== 'USD') {
        this.tipIntl = parseFloat((this.tip * this.currency.value).toFixed(2))
      }
    }
  },
  mounted: function () {
    if (this._getTotal2) {
      let val = this._getTotal2
      if (window.opoliCNS) {
        val.segment['Total Price'] = this.total

        if (val.key === 'Review Page') {
          val.segment['Promo Code'] = this.promoCode
          val.segment['Redeem'] = this.redeemTrigger ? 'Yes' : 'No'
        }
        window.opoliCNS('events', val)
      }
      this.$store.commit('tracking/setOpoliCnsData', {getTotal2: ''})
    }

    let self = this
    if (self.$store.state.TravelInsuranceLabel) {
      self.travelInsuranceLabel = self.$store.state.TravelInsuranceLabel
    }

    self.recheckTipLabel()

    self.$root.$on('redeemCodeFailed', function (message) {
    })

    self.$root.$on('invalidPromoCode', function (message) {
    })

    // if (['Customer', 'Payment'].indexOf(this.$route.name) > -1 && this.currentLeg === 'leg1') {
    //   let rsvp = this.$store.state.reservation
    //   let leg1 = rsvp.leg1
    //   if (CONFIG.Env == 'production' && window.gtag) { // eslint-disable-line
    //     gtag('event', `Total Price`, { // eslint-disable-line
    //       value: this.total
    //     })
    //   }
    //   if (CONFIG.Env == 'production' && window.gtag && this.$route.name == 'Payment') { // eslint-disable-line
    //     gtag('event', `Service Type`, { // eslint-disable-line
    //       value: leg1.services[leg1.selectedService].brandedName
    //     })
    //     gtag('event', `Pickup Type`, { // eslint-disable-line
    //       value: this.pickupTypeLabel(leg1.pickupType)
    //     })
    //     gtag('event', `Pickup Date - Leg1`, { // eslint-disable-line
    //       value: leg1.pickupDate
    //     })
    //     if (rsvp.isRoundtrip) {
    //       gtag('event', `Pickup Date - Leg2`, { // eslint-disable-line
    //         value: rsvp.leg2.pickupDate
    //       })
    //     }
    //   }
    // }

    if (this.isAsap) this.showTipForm = false
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/scss/Payment/PaymentSummaryForm.scss";
h2{ font-size: 1.5em!important; }
h2.total { 
  font-weight: 600!important; 
  padding-left: 4px;
}
.summary-value{
  padding-left: 4px;

  s{
    margin-right: 5px;
    position: relative;
    color: #bbb;

    &:after{
      content: '';
      border-bottom:1px solid rgba(255,0,0, .5);
      position: absolute;
      top:50%;
      left: 0;
      width: 100%;
    }
  }
}
.add-tip{
  margin-left: 10px;
}

@media only screen and (max-width: 40em) {
  .add-tip {
    text-decoration: underline;
    font-size: 15px;
  }

  .review-form {
    .payment-summary {
      .summary-label, .summary-value,
      .price-info .small-4 {
        width: 50%;
      }

      .summary-value.small-3 {
        width: 20% !important;
      }

      .small-5.medium-6 {
        width: 30% !important;
      }
    }
  }

}
</style>


// WEBPACK FOOTER //
// PaymentSummaryForm.vue?0e4011d2